import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Text, Pagination } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import { ActiveMonitor } from './ActiveMonitor';
import { MonitorsContainer, ReportMonitor } from './Components';
import { useReportMonitors } from '../useReportMonitors';
/**
 * This component shows a list of the user's active report monitors. It allows the user to turn monitoring on or off for a given report.
 */
export function MonitoredReportsModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { currentMonitors } = useReportMonitors();

  const queryClient = useQueryClient();

  const { isMobile } = useScreenSize();

  const [hasBeenDeleted, setHasBeenDeleted] = useState<{
    [key: string]: boolean;
  }>({});

  const [page, setPage] = useState(1);

  const [page1Reports, setPage1Reports] = useState<ReportMonitor[]>([]);
  const [page2Reports, setPage2Reports] = useState<ReportMonitor[]>([]);
  const [page3Reports, setPage3Reports] = useState<ReportMonitor[]>([]);
  const [page4Reports, setPage4Reports] = useState<ReportMonitor[]>([]);

  const reportsForPage = [
    page1Reports,
    page2Reports,
    page3Reports,
    page4Reports,
  ];

  const reportsPerPage = isMobile ? 3 : 5;

  const lastMonitorOnPageDeleted = useCallback(() => {
    return (
      currentMonitors && currentMonitors.length <= reportsPerPage * (page - 1)
    );
  }, [currentMonitors, reportsPerPage, page]);

  useEffect(() => {
    if (lastMonitorOnPageDeleted()) {
      if (page > 1) {
        setPage(page - 1);
      }
    }
  }, [lastMonitorOnPageDeleted, page]);

  useEffect(() => {
    const monitors = currentMonitors;
    if (monitors) {
      setPage1Reports(monitors.slice(0, reportsPerPage));
      setPage2Reports(monitors.slice(reportsPerPage, reportsPerPage * 2));
      setPage3Reports(monitors.slice(reportsPerPage * 2, reportsPerPage * 3));
      setPage4Reports(monitors.slice(reportsPerPage * 3));
    }
  }, [currentMonitors, reportsPerPage]);

  function needsPagination() {
    return (currentMonitors?.length || 0) > reportsPerPage;
  }

  function numPagesNeeded() {
    return Math.ceil(currentMonitors.length / reportsPerPage);
  }

  function handleClose() {
    queryClient.invalidateQueries({ queryKey: ['report_monitors'] });
    onClose();
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    pageNumber: number
  ) => {
    setPage(pageNumber);
  };

  return (
    <Modal
      title="Manage Monitored Reports"
      open={isOpen}
      aria-labelledby="manage-monitored-reports"
    >
      <Text variant="body2">
        Use the toggles to the right of each report to turn off monitoring.
        Changes will take effect immediately.
      </Text>
      <MonitorsContainer>
        {reportsForPage[page - 1].map((monitor: ReportMonitor) => (
          <ActiveMonitor
            key={monitor.id}
            monitor={monitor}
            hasBeenDeleted={hasBeenDeleted}
            setHasBeenDeleted={setHasBeenDeleted}
          />
        ))}
        {needsPagination() && (
          <Pagination
            page={page}
            count={numPagesNeeded()}
            onChange={(event, pageNumber) =>
              handlePageChange(event, pageNumber)
            }
            variant="outlined"
            shape="rounded"
            sx={{
              marginTop: 2,
              '& .MuiPaginationItem-page, & .MuiPaginationItem-ellipsis': {
                display: 'none',
              },
            }}
          />
        )}
      </MonitorsContainer>
      <Button
        variant="contained"
        onClick={() => handleClose()}
        sx={{ marginTop: 2, width: '100%' }}
        color="success"
      >
        Done
      </Button>
    </Modal>
  );
}
